import type { CSSProperties, ReactNode } from "react";

// Bottom tab bar for phone-width viewports (the shells render it instead of the top navigation
// when useIsMobile() is true). Fixed to the bottom edge, honours the iOS home-indicator inset,
// opaque so content scrolls underneath without bleeding through. One item per destination;
// an item is either a link (href) or a button (onClick). Icons are simple line glyphs drawn here.

export type NavIcon =
  | "overview" | "sheets" | "capital" | "members" | "profile"
  | "analytics" | "governance" | "compliance" | "finance";

export type BottomNavItem = {
  id: string;
  label: string;
  icon: NavIcon;
  active?: boolean;
  href?: string;
  onClick?: () => void;
  badge?: number | string | null;
};

const BAR_BG = "#0f131c";
const BORDER = "rgba(255,255,255,0.10)";
const MUTED = "#8A94A6";
const TEXT = "#E6E9EF";

function Glyph({ icon }: { icon: NavIcon }): ReactNode {
  const p = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (icon) {
    case "overview":
      return (<><rect x="3.5" y="3.5" width="7" height="7" rx="1.6" {...p} /><rect x="13.5" y="3.5" width="7" height="7" rx="1.6" {...p} />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" {...p} /><rect x="13.5" y="13.5" width="7" height="7" rx="1.6" {...p} /></>);
    case "sheets":
      return (<><path d="M7 3.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9.5A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z" {...p} />
        <path d="M14 3.5V8h4M8.5 12h7M8.5 15.5h7" {...p} /></>);
    case "capital":
      return (<><ellipse cx="12" cy="6.5" rx="7" ry="2.6" {...p} /><path d="M5 6.5v5c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-5" {...p} />
        <path d="M5 11.5v5c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-5" {...p} /></>);
    case "members":
      return (<><circle cx="9" cy="8.5" r="3" {...p} /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" {...p} />
        <circle cx="16.5" cy="9" r="2.4" {...p} /><path d="M15.5 14.2c2.8.2 5 2.1 5 4.8" {...p} /></>);
    case "profile":
      return (<><circle cx="12" cy="8.5" r="3.5" {...p} /><path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" {...p} /></>);
    case "analytics":
      return (<><path d="M4 20h16" {...p} /><path d="M6.5 16v-5M11 16V7M15.5 16v-3M20 16V5" {...p} /></>);
    case "governance":
      return (<><path d="M12 3.5l7 2.8v5.2c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6.3z" {...p} /><path d="M9 12.2l2.1 2.1 4-4.3" {...p} /></>);
    case "compliance":
      return (<><path d="M4.5 7h2M4.5 12h2M4.5 17h2" {...p} /><path d="M9.5 7h10M9.5 12h10M9.5 17h10" {...p} /></>);
    case "finance":
      return (<><path d="M4 8.5h13l-3-3M20 15.5H7l3 3" {...p} /></>);
    default:
      return null;
  }
}

export function BottomNav({ items, accent = "#6EA8FE" }: { items: BottomNavItem[]; accent?: string }) {
  const bar: CSSProperties = {
    position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 50,
    display: "flex", alignItems: "stretch",
    background: BAR_BG, borderTop: `1px solid ${BORDER}`,
    paddingBottom: "env(safe-area-inset-bottom)",
    boxShadow: "0 -8px 24px rgba(0,0,0,0.35)",
  };
  return (
    <nav className="bottom-nav" aria-label="Primary" style={bar}>
      {items.map((it) => {
        const color = it.active ? accent : MUTED;
        const inner: CSSProperties = {
          flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 3, padding: "9px 2px 7px", fontSize: 10.5, letterSpacing: 0.2, color, textDecoration: "none",
          background: "transparent", border: 0, cursor: it.active ? "default" : "pointer", fontFamily: "inherit",
          position: "relative", WebkitTapHighlightColor: "transparent",
        };
        const body = (<>
          <span style={{ position: "relative", display: "inline-flex", lineHeight: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><Glyph icon={it.icon} /></svg>
            {it.badge != null && it.badge !== 0 && it.badge !== "" && (
              <span style={{ position: "absolute", top: -4, right: -9, minWidth: 15, height: 15, padding: "0 4px", borderRadius: 999, background: accent, color: "#0a0e17", fontSize: 9.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{it.badge}</span>)}
          </span>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", fontWeight: it.active ? 600 : 400, color: it.active ? TEXT : MUTED }}>{it.label}</span>
        </>);
        return it.href
          ? <a key={it.id} href={it.href} aria-current={it.active ? "page" : undefined} style={inner}>{body}</a>
          : <button key={it.id} type="button" onClick={it.onClick} aria-current={it.active ? "page" : undefined} style={inner}>{body}</button>;
      })}
    </nav>
  );
}
